<script setup>
import Panel from '@maison/components/common/Panel.vue';
import PanelCloseButton from '@maison/components/common/PanelCloseButton.vue';
import SearchPanelResults from '@maison/components/search/SearchPanelResults.vue';
import SearchPanelEmptyState from '@maison/components/search/SearchPanelEmptyState.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useSearchPanel } from '@phoenix/composables/components/search/searchPanel';
import { MIN_TERM_LENGTH } from '@maison/helpers/search-helper';
import { closeAllPanels, showPanel } from '@phoenix/helpers/panel-helper';
import { useI18n } from 'vue-i18n';
import { rescue } from '@phoenix/helpers/common-helper';
import { PhxsgAutocomplete } from '@sg/src/View/Components/Molecules/Autocomplete';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  emptyState: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const {
  applyQuery,
  autocompleteRef,
  currentQuery,
  hasEmptyState,
  isQueryResultEmpty,
  searchInitialized,
  setCustomQuery,
  setQueryDebounced,
  setQueryFromUrl,
  stagedQuery,
  urlQueryTerm,
} = useSearchPanel();

const displayedTotalResults = ref(0);
const displaySuggestions = ref(false);

const title = computed(() => {
  return hasEmptyState.value
    ? t('Search.emptyState.title')
    : t('Search.results.title', {
        count: displayedTotalResults.value,
        query: stagedQuery.value,
      });
});

const suggestions = computed(() => {
  if (displaySuggestions.value && stagedQuery.value === currentQuery.value) {
    return store.state.search.suggestions;
  }

  return [];
});

const panelOptions = {
  closeAll: true,
  onShow: () => {
    searchInitialized.value = true;
  },
  onHide: () => {
    closeAllPanels([props.id]);
  },
  onHidden: () => {
    setCustomQuery('');
  },
};

const onQuery = (value) => {
  if (value === urlQueryTerm.value) {
    return;
  }

  displaySuggestions.value = true;
  setQueryDebounced(value);
};

const onEnter = async (value) => {
  if (!value || value.length < MIN_TERM_LENGTH) {
    return;
  }

  displaySuggestions.value = false;
  await applyQuery(value);
};

const onChange = async (option) => {
  if (!option && option.value) {
    return;
  }

  // prevent loading on url query set
  if (option.value === urlQueryTerm.value) {
    return;
  }

  rescue(() => {
    const event = window.tracking?.getSearchQuerySuggestion?.(option.value);
    if (event) {
      window.tracking.pushEvent(event);
    }
  });

  await applyQuery(option.value);
};

watch(
  () => store.getters['search/totalResults'],
  (value) => {
    if (value === 0) {
      return;
    }

    displayedTotalResults.value = value;
  },
);

onMounted(async () => {
  await setQueryFromUrl();

  if (urlQueryTerm.value) {
    showPanel(props.id);
  }
});
</script>

<template>
  <panel
    :id="id"
    fullscreen
    open-from="top"
    :classes="['search-panel']"
    :boxed-content="false"
    :should-display-header="false"
    :options="panelOptions"
  >
    <template #content>
      <div class="search-panel__search">
        <div id="search-panel-autocomplete-wrapper">
          <phxsg-autocomplete
            id="search-panel-autocomplete"
            ref="autocompleteRef"
            name="search-panel-autocomplete"
            :label="$t('Search.field.label')"
            :placeholder="$t('Search.field.placeholder')"
            loading-text=""
            no-result-text=""
            open-button-label=""
            clear-button-label=""
            :open-button-icon="false"
            :clear-button-icon="false"
            hide-label
            :auto-focus-first-option="false"
            :dropdown-offset="1"
            :min-characters="MIN_TERM_LENGTH"
            :min-rows="-1"
            :options="suggestions"
            close-on-input-blur
            data-cs-mask
            @autocomplete:query="onQuery"
            @autocomplete:change="onChange"
            @autocomplete:press-enter="onEnter"
          />
        </div>

        <panel-close-button :panel-id="id" icon="close" class="search-panel__close" />
      </div>

      <div v-if="searchInitialized" class="search-panel__content">
        <template v-if="isQueryResultEmpty">
          <h2 class="heading-medium box box--spacing-4 box--border-block-end" data-cs-mask>
            {{ $t('Search.noResult.title', { query: stagedQuery }) }}
          </h2>

          <div class="box box--spacing-4" v-html="$t('Search.noResult.text')" />
        </template>

        <h2 class="box box--spacing-4" data-cs-mask>{{ title }}</h2>

        <search-panel-empty-state v-show="hasEmptyState" :items="emptyState" />

        <search-panel-results v-if="!hasEmptyState" />
      </div>
    </template>
  </panel>
</template>
